import React, {forwardRef, Ref, useState} from 'react';
import styled from 'styled-components';

import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Skeleton} from '../../../../../libs/shared/ui/src/components/utility/skeleton';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {
  AnalyticsMetricError,
  AnalyticsScalarMetric,
  AnalyticsScalarMetricsEnum,
} from '../../../../models/api/analyticsMetricsModel';
import {getMetricRenderer} from '../../../helpers/analyticsFormatHelpers';
import {isZeroValidForScalarMetric} from '../../../helpers/isZeroValidForScalarMetric';
import {getMetricLabels} from '../../../helpers/metricLabels';
import {ReportElementIdsEnum} from '../../../helpers/reportHelpers/reportTypes';
import {computeMetricErrorTitle} from '../charts/common/errorHelpers';
import {AnalyticsTooltip} from '../tooltips/analyticsTooltips';
import {MetricVariation} from '../utils/metricVariation';
import {KeyMetricValue, KeyMetricValueSkeleton} from './keyMetricValue';

/*
 * Styles.
 */

// TODO: This component should not have margins and separators built in, that is the responsibility of the parent.
const StyledChromelessButton = styled(ChromelessButton)`
  padding: 31px 0;
  transition: background-color 0.1s;
  border-radius: 8px;

  margin: 8px -8px 0px -8px;
  padding: 6px 8px 4px;

  &:hover {
    background-color: ${(p) => p.theme.greys.shade20};
  }
`;

// TODO: This component should not have margins and separators built in, that is the responsibility of the parent.
const StyledKeyMetricWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  &:not(:last-child) {
    border-bottom: 1px solid ${(p) => p.theme.greys.shade30};

    ${StyledChromelessButton} {
      margin-bottom: 7px;
    }
  }
`;

const StyledKeyMetricGridDiv = styled.div`
  display: grid;
  grid-template-areas:
    'label label'
    'value variation';
  grid-template-columns: 1fr auto;
  grid-template-rows: auto 38px;
`;

const StyledMetricLabelDiv = styled.div`
  grid-area: label;

  ${flexCenter};
  gap: 4px;

  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  line-height: 16px;
  color: ${(p) => p.theme.greys.shade80};

  margin-bottom: 4px;
`;

const StyledMetricVariation = styled(MetricVariation)`
  grid-area: variation;

  line-height: 16px;
  align-self: end;
`;

const StyledKeyMetricValue = styled(KeyMetricValue)`
  grid-area: value;
`;

const StyledInertWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 14px;
  padding-bottom: 4px;
  &:not(:last-child) {
    padding-bottom: 11px;
    border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  }
`;

const StyledErrorTitleDiv = styled.div`
  height: 16px;
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledErrorSubtitleDiv = styled.div`
  height: 38px;
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Props.
 */

export interface KeyMetricProps {
  isLoading: boolean;
  reportSectionId: ReportElementIdsEnum; // Used for tracking purpose.
  metricData: AnalyticsScalarMetric | undefined;
  metricError: AnalyticsMetricError | undefined;
  onClick?: (metricId: AnalyticsScalarMetricsEnum) => void;
  hasTooltip?: boolean;
}

/*
 * Component.
 */

// TODO: Make this a presentational component. It should not do data tracking or depend on Redux.
export const KeyMetric = forwardRef<HTMLDivElement, KeyMetricProps>((props, ref) => {
  const {isLoading, reportSectionId, metricData, metricError, hasTooltip = true, onClick} = props;
  const [isHovered, setIsHovered] = useState(false);

  if (isLoading) {
    return renderLoadingState(ref);
  }

  if (!metricData) {
    const {title, subtitle} = computeMetricErrorTitle(metricError);

    return (
      <StyledInertWrapperDiv ref={ref}>
        <StyledErrorTitleDiv>{title}</StyledErrorTitleDiv>
        <StyledErrorSubtitleDiv>{subtitle}</StyledErrorSubtitleDiv>
      </StyledInertWrapperDiv>
    );
  }

  const {label, tooltip} = getMetricLabels(metricData.id);

  const metricRenderer = getMetricRenderer(metricData.type);

  return (
    <StyledKeyMetricWrapperDiv>
      <StyledChromelessButton
        onClick={() => onClick?.(metricData.id)}
        onFocus={() => hasTooltip && setIsHovered(true)}
        onMouseEnter={() => hasTooltip && setIsHovered(true)}
        onMouseLeave={() => hasTooltip && setIsHovered(false)}
      >
        <StyledKeyMetricGridDiv ref={ref}>
          <StyledMetricLabelDiv>
            {label}
            {isHovered ? <AnalyticsTooltip tooltip={tooltip} reportSectionId={reportSectionId} /> : undefined}
          </StyledMetricLabelDiv>
          <StyledKeyMetricValue>
            {metricRenderer({
              value: metricData.value,
              isCompact: true,
              compactThreshold: 100_000,
              isZeroValid: isZeroValidForScalarMetric(metricData),
            })}
          </StyledKeyMetricValue>
          <StyledMetricVariation metricData={metricData} displayRawVariation />
        </StyledKeyMetricGridDiv>
      </StyledChromelessButton>
    </StyledKeyMetricWrapperDiv>
  );
});

/*
 * Helpers.
 */

function renderLoadingState(ref: Ref<HTMLDivElement>) {
  return (
    <StyledInertWrapperDiv ref={ref}>
      <Skeleton width="123px" height="16px" />
      <KeyMetricValueSkeleton />
    </StyledInertWrapperDiv>
  );
}
